import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getImportedBaseAvailability,
  getStoredImportedBase,
} from "@/app/actions/imported-profiles/action";
import { ImportedBaseAvailability } from "@/app/types/imported-profile/ImportedBaseSearch";
import { QueryKey } from "@/app/vars/QueryKeys";

type Options = {
  avatarId?: number;
  enabled?: boolean;
};

export function buildImportedBaseStoredKey(avatarId?: number) {
  return [QueryKey.IMPORTED_BASE, avatarId, "stored"];
}

// What the button reads at page load: the numbers a previous run left in the row, no scan.
export function useFetchStoredImportedBase(options: Options) {
  const { avatarId, enabled = true } = options;

  return useQuery({
    queryKey: buildImportedBaseStoredKey(avatarId),
    queryFn: () => getStoredImportedBase(avatarId!),
    enabled: enabled && !!avatarId,
    staleTime: Infinity,
  });
}

// What the modal reads when it opens: the signature decides whether anything is recomputed.
export function useFetchImportedBase(options: Options) {
  const { avatarId, enabled = true } = options;
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: [QueryKey.IMPORTED_BASE, avatarId, "live"],
    queryFn: async () => {
      const availability = await getImportedBaseAvailability(avatarId!);

      queryClient.setQueryData<ImportedBaseAvailability>(
        buildImportedBaseStoredKey(avatarId),
        availability,
      );

      return availability;
    },
    enabled: enabled && !!avatarId,
    // Le bouton a déjà les chiffres du dernier calcul: on les montre tout de suite et on
    // revérifie derrière, plutôt que de faire attendre un aller-retour pour souvent rien.
    initialData: () =>
      queryClient.getQueryData<ImportedBaseAvailability>(buildImportedBaseStoredKey(avatarId)),
    refetchOnMount: "always",
    staleTime: (query) => (query.state.data?.refreshing ? 0 : Infinity),
  });
}
